import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";

// Redirect old English product URLs (/product/:id) to the canonical Lithuanian path (/produktas/:id) for SEO.
// Preserve query string (UTM/gclid) and hash (anchors) so marketing params and deep links survive the redirect.
const ProductRedirect = () => {
  const { id } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/produktas/${id}${search}${hash}`} replace />;
};

// Legacy plural URL redirect (/products/:id, /produktai/:id) to the canonical /produktas/:id path for SEO.
// Preserve query string and hash across the redirect.
const ProductsRedirect = () => {
  const { id } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/produktas/${id}${search}${hash}`} replace />;
};
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { AuthProvider } from "./contexts/AuthContext";
import { StripeProvider } from "./components/StripeProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Header from "./components/Header";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import MobileBottomNav from "./components/MobileBottomNav";
import HtmlLangUpdater from "./components/HtmlLangUpdater";
import SocialProofPopup from "./components/SocialProofPopup";
import SeasonalEffects from "./components/SeasonalEffects";
import GlobalRecentlyViewed from "./components/GlobalRecentlyViewed";

// Lazy load pages for better code splitting
const Index = lazy(() => import("./pages/Index"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Pristatymas = lazy(() => import("./pages/Pristatymas"));
const Grazinimas = lazy(() => import("./pages/Grazinimas"));
const DUK = lazy(() => import("./pages/DUK"));
const SlapukuPolitika = lazy(() => import("./pages/SlapukuPolitika"));
const PrivatumuPolitika = lazy(() => import("./pages/PrivatumuPolitika"));
const NaudojimoSalygos = lazy(() => import("./pages/NaudojimoSalygos"));
const PrivacySettings = lazy(() => import("./pages/PrivacySettings"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin components
const AdminRouteWrapper = lazy(() => import("./components/admin/AdminRouteWrapper").then(m => ({ default: m.AdminRouteWrapper })));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminOnlyRoute = lazy(() => import("./components/admin/AdminOnlyRoute").then(m => ({ default: m.AdminOnlyRoute })));
const AdminHero = lazy(() => import("./pages/admin/AdminHero").then(m => ({ default: m.AdminHero })));
const AdminSalonLocations = lazy(() => import("./pages/admin/AdminSalonLocations").then(m => ({ default: m.AdminSalonLocations })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts").then(m => ({ default: m.AdminProducts })));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories").then(m => ({ default: m.AdminCategories })));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders").then(m => ({ default: m.AdminOrders })));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers").then(m => ({ default: m.AdminUsers })));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers").then(m => ({ default: m.AdminSubscribers })));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings").then(m => ({ default: m.AdminSettings })));
const AdminLoyaltyPoints = lazy(() => import("./pages/admin/AdminLoyaltyPoints").then(m => ({ default: m.AdminLoyaltyPoints })));
const AdminShippingSettings = lazy(() => import("./pages/admin/AdminShippingSettings").then(m => ({ default: m.AdminShippingSettings })));
const AdminOmnivaSettings = lazy(() => import("./pages/admin/AdminOmnivaSettings").then(m => ({ default: m.AdminOmnivaSettings })));
const AdminContactMessages = lazy(() => import("./pages/admin/AdminContactMessages").then(m => ({ default: m.AdminContactMessages })));
const AdminCarts = lazy(() => import("./pages/admin/AdminCarts"));
const AdminBrands = lazy(() => import("./pages/admin/AdminBrands"));
const AdminSuppliers = lazy(() => import("./pages/admin/AdminSuppliers"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminSkinTypes = lazy(() => import("./pages/admin/AdminSkinTypes").then(m => ({ default: m.AdminSkinTypes })));
const AdminPaskirtis = lazy(() => import("./pages/admin/AdminPaskirtis").then(m => ({ default: m.AdminPaskirtis })));
const AdminSupplierOrders = lazy(() => import("./pages/admin/AdminSupplierOrders"));
const AdminEmailMarketing = lazy(() => import("./pages/admin/AdminEmailMarketing").then(m => ({ default: m.AdminEmailMarketing })));
const AdminProductTranslations = lazy(() => import("./pages/admin/AdminProductTranslations"));
const AdminFeatures = lazy(() => import("./pages/admin/AdminFeatures"));
const AdminStripeSettings = lazy(() => import("./pages/admin/AdminStripeSettings"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminSpecialists = lazy(() => import("./pages/admin/AdminSpecialists"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminDiscountCodes = lazy(() => import("./pages/admin/AdminDiscountCodes"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics").then(m => ({ default: m.AdminStatistics })));
const Booking = lazy(() => import("./pages/Booking"));
const AIOdosKonsultante = lazy(() => import("./pages/AIOdosKonsultante"));
const BeautyGame = lazy(() => import("./pages/BeautyGame"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SpecialistDashboard = lazy(() => import("./pages/specialist/SpecialistDashboard"));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection time
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <WishlistProvider>
              <CartProvider>
                <StripeProvider>
                  <Toaster />
                  <Sonner />
                  <ScrollToTop />
                  <HtmlLangUpdater />
                  <div className="min-h-screen bg-background pb-20 lg:pb-0">
                    <Header />
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/product/:id" element={<ProductRedirect />} />
                          <Route path="/products/:id" element={<ProductsRedirect />} />
                          <Route path="/produktas/:id" element={<ProductDetail />} />
                          <Route path="/produktai/:id" element={<ProductsRedirect />} />
                          <Route path="/kategorija/:slug" element={<CategoryPage />} />
                          <Route path="/checkout" element={<Checkout />} />
                          <Route path="/payment-success" element={<PaymentSuccess />} />
                          <Route path="/auth" element={<Auth />} />
                          <Route path="/account" element={<Account />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<BlogPost />} />
                          <Route path="/pristatymas" element={<Pristatymas />} />
                          <Route path="/grazinimas" element={<Grazinimas />} />
                          <Route path="/duk" element={<DUK />} />
                          <Route path="/slapuku-politika" element={<SlapukuPolitika />} />
                          <Route path="/privatumo-politika" element={<PrivatumuPolitika />} />
                          <Route path="/naudojimo-salygos" element={<NaudojimoSalygos />} />
                          <Route path="/privatumo-nustatymai" element={<PrivacySettings />} />
                          <Route path="/booking" element={<Booking />} />
                          <Route path="/ai-odos-konsultante" element={<AIOdosKonsultante />} />
                          <Route path="/beauty-game" element={<BeautyGame />} />
                          <Route path="/unsubscribe" element={<Unsubscribe />} />
                          <Route path="/atsisakyti-prenumeratos" element={<Unsubscribe />} />
                          <Route path="/specialist" element={<SpecialistDashboard />} />
                          
                          {/* Admin Routes */}
                          <Route path="/admin" element={<AdminRouteWrapper><AdminLayout><AdminDashboard /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/hero" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminHero /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/products" element={<AdminRouteWrapper><AdminLayout><AdminProducts /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/categories" element={<AdminRouteWrapper><AdminLayout><AdminCategories /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/skin-types" element={<AdminRouteWrapper><AdminLayout><AdminSkinTypes /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/paskirtis" element={<AdminRouteWrapper><AdminLayout><AdminPaskirtis /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/brands" element={<AdminRouteWrapper><AdminLayout><AdminBrands /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/suppliers" element={<AdminRouteWrapper><AdminLayout><AdminSuppliers /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/orders" element={<AdminRouteWrapper><AdminLayout><AdminOrders /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/supplier-orders" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminSupplierOrders /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/loyalty-points" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminLoyaltyPoints /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/users" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminUsers /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/subscribers" element={<AdminRouteWrapper><AdminLayout><AdminSubscribers /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/contact-messages" element={<AdminRouteWrapper><AdminLayout><AdminContactMessages /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/carts" element={<AdminRouteWrapper><AdminLayout><AdminCarts /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/invoices" element={<AdminRouteWrapper><AdminLayout><AdminInvoices /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/marketing-emails" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminEmailMarketing /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/settings" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminSettings /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/shipping-settings" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminShippingSettings /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/salon-locations" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminSalonLocations /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/omniva-settings" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminOmnivaSettings /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/stripe-settings" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminStripeSettings /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/translations" element={<AdminRouteWrapper><AdminLayout><AdminProductTranslations /></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/features" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminFeatures /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/services" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminServices /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/specialists" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminSpecialists /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/bookings" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminBookings /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/discount-codes" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminDiscountCodes /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          <Route path="/admin/statistics" element={<AdminRouteWrapper><AdminLayout><AdminOnlyRoute><AdminStatistics /></AdminOnlyRoute></AdminLayout></AdminRouteWrapper>} />
                          
                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      <GlobalRecentlyViewed />
                      <CookieBanner />
                      <MobileBottomNav />
                      <SocialProofPopup />
                      <SeasonalEffects />
                    </div>
                  </StripeProvider>
                </CartProvider>
              </WishlistProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );

export default App;